import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import memberPermissionService from '@/services/member-permissions.service';
import { useToast } from '@/components/ui/atoms/MToastProvider';

interface RequestAccessParams {
  memberId: string;
  familyId: string;
  familyMemberId: string;
}

interface MemberClickParams {
  memberId: string;
  loggedInMemberId: string;
  loggedInMemberType: string;
  managedIds: string[] | null | undefined;
  managedLoading: boolean;
  onNoAccess: () => void;
  backUrl?: string;
}

export function useMemberAccess() {
  const navigate = useNavigate();
  const [requestingAccess, setRequestingAccess] = useState(false);
  const { showToast } = useToast();

  const requestAccess = useCallback(
    async (params: RequestAccessParams) => {
      if (!params.memberId || !params.familyId || !params.familyMemberId) {
        console.error('Missing required parameters for request access');
        return { success: false };
      }

      setRequestingAccess(true);
      try {
        await memberPermissionService.requestAccess({
          memberId: params.memberId,
          familyId: params.familyId,
          familyMemberId: params.familyMemberId,
        });

        showToast({
          message: 'Access request sent to Family Owner',
          variant: 'success',
        });

        return { success: true };
      } catch (error) {
        console.error('Error requesting access:', error);
        showToast({
          message: 'Failed to send access request. Please try again.',
          variant: 'error',
        });
        return { success: false };
      } finally {
        setRequestingAccess(false);
      }
    },
    [showToast]
  );

  const hasAccess = useCallback(
    (memberId: string, managedIds: string[] | null | undefined): boolean => {
      // If managedIds is not an array or is null/undefined, return false
      if (!Array.isArray(managedIds)) {
        return false;
      }
      // Check if the memberId is in the managedIds array
      return managedIds.includes(memberId);
    },
    []
  );

  const handleMemberClick = useCallback(
    (params: MemberClickParams) => {
      const {
        memberId,
        loggedInMemberId,
        loggedInMemberType,
        managedIds,
        managedLoading,
        onNoAccess,
        backUrl = '/profile/family', // Default fallback
      } = params;

      // Always allow clicking on own profile
      if (memberId === loggedInMemberId) {
        navigate(`/profile/family/member/${memberId}`, {
          state: { backUrl }
        });
        return;
      }

      // For Family Owner, always allow access to all members
      if (loggedInMemberType === 'fo') {
        navigate(`/profile/family/member/${memberId}`, {
          state: { backUrl }
        });
        return;
      }

      // For Family Member, check if they have access to this member
      if (loggedInMemberType === 'fm') {
        // If managedIds is still loading, don't do anything
        if (managedLoading) {
          return;
        }

        // Check if this member is in the managedIds array
        const memberHasAccess = hasAccess(memberId, managedIds);

        if (memberHasAccess) {
          // FM can manage this member, navigate to detail page
          navigate(`/profile/family/member/${memberId}`, {
            state: { backUrl }
          });
        } else {
          // FM cannot manage this member, show no access dialog
          onNoAccess();
        }
        return;
      }

      // Default: navigate to member detail
      navigate(`/profile/family/member/${memberId}`, {
        state: { backUrl }
      });
    },
    [navigate, hasAccess]
  );

  const handleRequestAccessWithCleanup = useCallback(
    async (
      params: RequestAccessParams,
      onSuccess?: () => void
    ): Promise<{ success: boolean }> => {
      if (!params.memberId || !params.familyId || !params.familyMemberId) {
        console.error('Missing required IDs for request access');
        return { success: false };
      }

      const result = await requestAccess(params);

      if (result.success && onSuccess) {
        onSuccess();
      }

      return result;
    },
    [requestAccess]
  );

  return {
    requestAccess,
    requestingAccess,
    hasAccess,
    handleMemberClick,
    handleRequestAccessWithCleanup,
  };
}
